import {
  Box,
  FormControl,
  FormControlProps,
  Grid,
  styled,
  Tooltip,
  Typography,
  TypographyProps,
} from '@mui/material'
import { FC } from 'react'
import PdfIcon from '@/assets/icons/pdf-icon.png'
import Mp4Icon from '@/assets/icons/mp4-icon.png'
import ImageIcon from '@/assets/icons/image-icon.png'
import theme from '@/utilities/Theme'
import { useOpenAttachment } from '@/utilities'

interface FormSecondStepProps {
  formData: any
  role: string
  type: any
}

const CustomLabel = styled(Typography)<TypographyProps>(({ theme }) => ({
  color: '#505470',
  fontWeight: 500,
  fontSize: '16px',
}))

const CustomGrid = styled(Grid)({
  display: 'flex',
  flexDirection: 'row',
  marginLeft: '5%',
  marginRight: '5%',
  justifyContent: 'space-between',
  marginTop: '3%',
  flexWrap: 'wrap',
})

const CustomDataLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  color: '#231F20',
  marginLeft: '20px',
  marginTop: '10px',
  fontSize: '16px',
  [theme.breakpoints.down('md')]: {
    marginTop: 0,
  },
}))

const CustomFormControl = styled(FormControl)<FormControlProps>(
  ({ theme }) => ({
    width: '45%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  }),
)

export const FormSecondStep: FC<FormSecondStepProps> = ({ formData, type }) => {
  const getFileInstance = (file: any) => {
    if (file instanceof File) return file

    return new File([file.url], file.fileName, { type: file.fileType })
  }

  const openAttachment = useOpenAttachment()

  return (
    <Box width={'100%'} height={'100%'} sx={{ overflowX: 'hidden' }}>
      <CustomGrid>
        <CustomFormControl>
          <CustomLabel>Dealer Name</CustomLabel>
          <CustomDataLabel>{formData?.selectedDealer}</CustomDataLabel>
        </CustomFormControl>
        <CustomFormControl>
          <CustomLabel>Branch Name</CustomLabel>
          <CustomDataLabel>
            {formData?.selectedBranchDealer?.branchName}
          </CustomDataLabel>
        </CustomFormControl>
      </CustomGrid>

      <CustomGrid>
        <CustomFormControl>
          <CustomLabel>Requestor Name</CustomLabel>
          <CustomDataLabel>{formData?.requestorName}</CustomDataLabel>
        </CustomFormControl>
        <CustomFormControl>
          <CustomLabel>Shop Name</CustomLabel>
          <CustomDataLabel>
            {formData?.selectedBranchDealer?.shop}
          </CustomDataLabel>
        </CustomFormControl>
      </CustomGrid>

      <CustomGrid>
        <CustomFormControl>
          <CustomLabel>Area</CustomLabel>
          <CustomDataLabel>{formData?.area}</CustomDataLabel>
        </CustomFormControl>
        <CustomFormControl>
          <CustomLabel>Province</CustomLabel>
          <CustomDataLabel>
            {formData.selectedBranchDealer.provinceName}
          </CustomDataLabel>
        </CustomFormControl>
      </CustomGrid>

      <CustomGrid>
        <CustomFormControl>
          <CustomLabel>Type of Concern</CustomLabel>
          <CustomDataLabel>{formData?.typeOfConcern}</CustomDataLabel>
        </CustomFormControl>
        <CustomFormControl>
          <CustomLabel>{`Type of ${formData?.typeOfConcern}`}</CustomLabel>
          <CustomDataLabel>
            {formData?.typeOfConcernValue !== 'Others'
              ? formData?.typeOfConcernValue
              : formData?.typeOfConcernOthers}
          </CustomDataLabel>
        </CustomFormControl>
      </CustomGrid>

      {/* <CustomGrid>
        <CustomFormControl>
          <CustomLabel>Purpose</CustomLabel>
          <CustomDataLabel>{formData?.purpose}</CustomDataLabel>
        </CustomFormControl>
      </CustomGrid> */}

      {/* <CustomGrid>
        <CustomFormControl>
          <CustomLabel>Method of Repair</CustomLabel>
          <CustomDataLabel>
            {(formData?.methodOfRepair ?? formData?.selectedRepairMethod)?.replace(/^C\/O/, 'c/o')}
          </CustomDataLabel>
        </CustomFormControl>
        <CustomFormControl>
          <CustomLabel>Cost Sharing</CustomLabel>
          <CustomDataLabel>{formData?.costOfSharing}</CustomDataLabel>
        </CustomFormControl>
      </CustomGrid> */}

      <CustomGrid>
        <CustomFormControl>
          <CustomLabel>Cost Amount</CustomLabel>
          <CustomDataLabel>
            {formData.costAmount === '' ? '0.000' : formData.costAmount}
          </CustomDataLabel>
        </CustomFormControl>
      </CustomGrid>

      <CustomGrid>
        <CustomFormControl>
          <CustomLabel>Description</CustomLabel>
          <CustomDataLabel>{formData.description}</CustomDataLabel>
        </CustomFormControl>
      </CustomGrid>

      <CustomGrid>
        <FormControl sx={{ width: '100%' }}>
          <CustomLabel>Concern Attachments</CustomLabel>
          <Grid container spacing={2}>
            {formData.concernAttachments
              ?.sort((a: any, b: any) => a.id - b.id)
              ?.map((file: any, index: number) => {
                const fileInstance = getFileInstance(file)
                const fileType = fileInstance.type
                const fileUrl = file.url || URL.createObjectURL(fileInstance)
                const fileName = fileInstance
                  ? fileInstance.name
                  : file.fileName

                return (
                  <Grid
                    container
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      marginTop: '1rem',
                    }}
                  >
                    {fileType?.startsWith('image/') ? (
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        width={'100%'}
                        height={'25px'}
                      >
                        <Grid
                          display={'flex'}
                          flexDirection={'row'}
                          alignItems={'center'}
                          gap={1}
                        >
                          <img
                            src={ImageIcon}
                            alt="Preview"
                            width={25}
                            height={25}
                            style={{ objectFit: 'cover' }}
                          />
                          <Tooltip
                            title={fileName}
                            arrow
                            onClick={() => {
                              if (type === 'Update') {
                                openAttachment(fileName)
                              } else {
                                return
                              }
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: '120px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                              }}
                            >
                              <a
                                target="_blank"
                                href={fileUrl}
                                style={{ color: '#000', fontWeight: 500 }}
                              >
                                {fileName}
                              </a>
                            </Typography>
                          </Tooltip>
                        </Grid>
                      </Box>
                    ) : fileType === 'application/pdf' ? (
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        width={'100%'}
                        height={'25px'}
                      >
                        <Grid
                          display={'flex'}
                          flexDirection={'row'}
                          alignItems={'center'}
                          gap={1}
                        >
                          <img
                            src={PdfIcon}
                            alt="PDF"
                            width={25}
                            height={25}
                            style={{ objectFit: 'cover' }}
                          />
                          <Tooltip
                            title={fileName}
                            arrow
                            onClick={() => {
                              if (type === 'Update') {
                                openAttachment(fileName)
                              } else {
                                return
                              }
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: '120px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                              }}
                            >
                              <a
                                target="_blank"
                                href={fileUrl}
                                style={{ color: '#000', fontWeight: 500 }}
                              >
                                {fileName}
                              </a>
                            </Typography>
                          </Tooltip>
                        </Grid>
                      </Box>
                    ) : fileType === 'video/mp4' ? (
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        width={'100%'}
                        height={'25px'}
                      >
                        <Grid
                          display={'flex'}
                          flexDirection={'row'}
                          alignItems={'center'}
                          gap={1}
                        >
                          <img
                            src={Mp4Icon}
                            alt="MP4"
                            width={25}
                            height={25}
                            style={{ objectFit: 'cover' }}
                          />
                          <Tooltip
                            title={fileName}
                            arrow
                            onClick={() => {
                              if (type === 'Update') {
                                openAttachment(fileName)
                              } else {
                                return
                              }
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: '120px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                              }}
                            >
                              <a
                                target="_blank"
                                href={fileUrl}
                                style={{ color: '#000', fontWeight: 500 }}
                              >
                                {fileName}
                              </a>
                            </Typography>
                          </Tooltip>
                        </Grid>
                      </Box>
                    ) : null}
                  </Grid>
                )
              })}
          </Grid>
        </FormControl>
      </CustomGrid>

      {formData?.purpose === 'Reimbursement' && (
        <CustomGrid>
          <FormControl sx={{ width: '100%' }}>
            <CustomLabel>
              Quotation / Certificate of Completion / Official Receipt
              Attachments
            </CustomLabel>
            <Grid container>
              {formData.quotationAttachments?.map(
                (file: any, index: number) => {
                  const fileInstance = getFileInstance(file)
                  const fileType = fileInstance.type
                  const fileUrl = file.url || URL.createObjectURL(fileInstance)
                  const fileName = fileInstance
                    ? fileInstance.name
                    : file.fileName

                  return (
                    <Grid
                      container
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        marginTop: '1rem',
                      }}
                    >
                      {fileType?.startsWith('image/') ? (
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          width={'100%'}
                          height={'10px'}
                        >
                          <Grid
                            display={'flex'}
                            flexDirection={'row'}
                            alignItems={'center'}
                            gap={1}
                          >
                            <img
                              src={ImageIcon}
                              alt="Preview"
                              width={25}
                              height={25}
                              style={{ objectFit: 'cover' }}
                            />
                            <Tooltip
                              title={fileName}
                              arrow
                              onClick={() => {
                                if (type === 'Update') {
                                  openAttachment(fileName)
                                } else {
                                  return
                                }
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: '120px',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  cursor: 'pointer',
                                }}
                              >
                                <a
                                  target="_blank"
                                  href={fileUrl}
                                  style={{ color: '#000', fontWeight: 500 }}
                                >
                                  {fileName}
                                </a>
                              </Typography>
                            </Tooltip>
                          </Grid>
                        </Box>
                      ) : fileType === 'application/pdf' ? (
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          width={'100%'}
                            height={'25px'}
                        >
                          <Grid
                            display={'flex'}
                            flexDirection={'row'}
                            alignItems={'center'}
                            gap={1}
                          >
                            <img
                              src={PdfIcon}
                              alt="PDF"
                              width={25}
                              height={25}
                              style={{ objectFit: 'cover' }}
                            />
                            <Tooltip
                              title={fileName}
                              arrow
                              onClick={() => {
                                if (type === 'Update') {
                                  openAttachment(fileName)
                                } else {
                                  return
                                }
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: '120px',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  cursor: 'pointer',
                                }}
                              >
                                <a
                                  target="_blank"
                                  href={fileUrl}
                                  style={{ color: '#000', fontWeight: 500 }}
                                >
                                  {fileName}
                                </a>
                              </Typography>
                            </Tooltip>
                          </Grid>
                        </Box>
                      ) : fileType === 'video/mp4' ? (
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          width={'100%'}
                            height={'25px'}
                        >
                          <Grid
                            display={'flex'}
                            flexDirection={'row'}
                            alignItems={'center'}
                            gap={1}
                          >
                            <img
                              src={Mp4Icon}
                              alt="MP4"
                              width={25}
                              height={25}
                              style={{ objectFit: 'cover' }}
                            />
                            <Tooltip
                              title={fileName}
                              arrow
                              onClick={() => {
                                if (type === 'Update') {
                                  openAttachment(fileName)
                                } else {
                                  return
                                }
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: '120px',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  cursor: 'pointer',
                                }}
                              >
                                <a
                                  target="_blank"
                                  href={fileUrl}
                                  style={{ color: '#000', fontWeight: 500 }}
                                >
                                  {fileName}
                                </a>
                              </Typography>
                            </Tooltip>
                          </Grid>
                        </Box>
                      ) : null}
                    </Grid>
                  )
                },
              )}
            </Grid>
          </FormControl>
        </CustomGrid>
      )}
    </Box>
  )
}
